import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Snowflake,
  LayoutDashboard,
  Settings,
  BookOpen,
  GitBranch,
  DollarSign,
  Layers,
  TrendingUp,
  ChevronDown,
} from "lucide-react";
import { useInbox } from "@/hooks/useInbox";
import logo from "@/assets/brand/simbolo.svg";

type BadgeTone = "bad" | "warn" | "neutral";

type Item = {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  /** Fixed badge tone per nav item — matches the redesign mockup's per-section coloring. */
  badgeTone?: BadgeTone;
};

// "Comando" is intentionally ungrouped (no uppercase label above it) — it's
// the single top-level entry, per the redesign mockup.
const topItem: Item = { title: "Comando", url: "/admin", icon: LayoutDashboard, badgeTone: "bad" };

const groups: { label: string; items: Item[] }[] = [
  {
    label: "Comercial",
    items: [
      { title: "Leads frios", url: "/admin/leads-frios", icon: Snowflake },
      { title: "Leads & propostas", url: "/admin/pipeline", icon: GitBranch, badgeTone: "neutral" },
      { title: "Relatório de vendas", url: "/admin/sales", icon: TrendingUp },
    ],
  },
  {
    label: "Entrega",
    items: [
      // NEW route — built in Task 7 (Projetos hub). Wired now per the redesign
      // rollout plan; 404s until then, which is expected.
      { title: "Projetos", url: "/admin/projetos", icon: Layers, badgeTone: "warn" },
      { title: "Financeiro", url: "/admin/finance", icon: DollarSign },
    ],
  },
  {
    label: "Presença",
    items: [
      // NEW route — built in Task 7 (Conteúdo hub, replaces the 5 standalone
      // Blog/Produtos/Depoimentos/Links/Categorias entries).
      { title: "Conteúdo", url: "/admin/conteudo", icon: BookOpen },
    ],
  },
];

const footerItem: Item = { title: "Configurações", url: "/admin/settings", icon: Settings };

const allGroups = [
  { label: "Comando", items: [topItem] },
  ...groups,
  { label: "", items: [footerItem] },
];
const allUrls = allGroups.flatMap((g) => g.items.map((i) => i.url));

const SIDEBAR_GROUPS_STORAGE_KEY = "admin-sidebar-groups";

function loadOpenGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const badgeToneClasses: Record<BadgeTone, string> = {
  bad: "bg-bad-soft text-bad",
  warn: "bg-warn-soft text-warn",
  neutral: "bg-muted text-muted-foreground",
};

function useNavBadges() {
  const { data: inbox } = useInbox();

  const pendingTotal = inbox?.length ?? 0;
  const leadPending = inbox?.filter((i) => i.kind === "lead_no_response").length ?? 0;
  const budgetPending = inbox?.filter((i) => i.kind.startsWith("budget")).length ?? 0;
  const projectPending =
    inbox?.filter((i) => i.kind === "project_overdue" || i.kind === "project_review_stuck")
      .length ?? 0;

  const badgeFor = (url: string): number => {
    if (url === "/admin") return pendingTotal;
    if (url === "/admin/pipeline") return leadPending + budgetPending;
    if (url === "/admin/projetos") return projectPending;
    return 0;
  };

  return badgeFor;
}

interface AdminSidebarNavProps {
  /** Icon-only mode (desktop collapsed state). Mobile Sheet always passes false. */
  collapsed: boolean;
  /** Called after a nav link is clicked — used to close the mobile Sheet. */
  onNavigate?: () => void;
}

/**
 * Shell nav content (logo header, grouped links, footer). Kept separate from
 * the `<Sidebar>` positioning wrapper so it can be reused verbatim inside a
 * mobile `Sheet` (see Admin.tsx) without dragging along the desktop
 * fixed/icon-collapse behavior.
 */
export function AdminSidebarNav({ collapsed, onNavigate }: AdminSidebarNavProps) {
  const { pathname } = useLocation();
  const badgeFor = useNavBadges();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadOpenGroups);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleGroup = (label: string) =>
    setOpenGroups((prev) => ({ ...prev, [label]: !(prev[label] ?? true) }));

  const isActive = (path: string) => {
    if (path === "/admin") return pathname === "/admin";
    if (!pathname.startsWith(path)) return false;
    if (pathname === path) return true;
    const hasMoreSpecificMatch = allUrls.some(
      (u) => u !== path && u.startsWith(path + "/") && pathname.startsWith(u),
    );
    return !hasMoreSpecificMatch;
  };

  const navItemCls = (path: string) =>
    isActive(path)
      ? "bg-accent text-primary font-semibold"
      : "text-muted-foreground hover:bg-muted hover:text-foreground";

  const renderItem = (item: Item) => {
    const count = badgeFor(item.url);
    const tone = item.badgeTone ?? "neutral";
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton asChild>
          <NavLink
            to={item.url}
            end={item.url === "/admin"}
            onClick={onNavigate}
            className={`h-[38px] gap-[11px] rounded-[9px] px-3 text-[13.5px] transition-colors ${navItemCls(item.url)}`}
          >
            <div className="relative shrink-0">
              <item.icon className="h-[17px] w-[17px]" strokeWidth={1.8} />
              {collapsed && count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-bad" />
              )}
            </div>
            {!collapsed && <span className="flex-1">{item.title}</span>}
            {!collapsed && count > 0 && (
              <span
                className={`ml-auto inline-flex h-[19px] min-w-5 items-center justify-center rounded-md px-[7px] text-[11px] font-bold ${badgeToneClasses[tone]}`}
              >
                {count}
              </span>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-[11px] px-5 pb-[18px] pt-[22px]">
        <img
          src={logo}
          alt="Eliveuton Souza"
          className="h-[34px] w-[34px] shrink-0 object-contain"
        />
        {!collapsed && (
          <div className="min-w-0 leading-[1.25]">
            <div className="truncate text-sm font-bold tracking-tight">Eliveuton Souza</div>
            <div className="truncate text-[11.5px] tracking-[0.02em] text-muted-foreground">
              Painel administrativo
            </div>
          </div>
        )}
      </div>

      <nav className="flex flex-1 flex-col gap-[22px] overflow-auto px-3 pb-3 pt-1">
        {/* Comando — single item, no group label */}
        <SidebarMenu className="gap-0.5">{renderItem(topItem)}</SidebarMenu>

        {groups.map((group) => {
          const items = (
            <SidebarMenu className="gap-0.5">{group.items.map(renderItem)}</SidebarMenu>
          );

          if (collapsed) {
            return (
              <SidebarGroup key={group.label} className="p-0">
                <SidebarGroupContent>{items}</SidebarGroupContent>
              </SidebarGroup>
            );
          }

          const isOpen = openGroups[group.label] ?? true;
          return (
            <SidebarGroup key={group.label} className="p-0">
              <Collapsible open={isOpen} onOpenChange={() => toggleGroup(group.label)}>
                <CollapsibleTrigger asChild>
                  <SidebarGroupLabel className="group/label flex h-auto cursor-pointer select-none items-center justify-between px-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-muted-foreground hover:text-foreground">
                    {group.label}
                    <ChevronDown
                      className={`h-3.5 w-3.5 opacity-0 transition-all group-hover/label:opacity-100 ${isOpen ? "" : "-rotate-90 opacity-100"}`}
                    />
                  </SidebarGroupLabel>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>{items}</SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          );
        })}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <SidebarMenu className="gap-0.5">{renderItem(footerItem)}</SidebarMenu>
      </div>
    </div>
  );
}

export function AdminSidebar() {
  const sidebar = useSidebar();
  const collapsed = sidebar.state === "collapsed";

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarContent className="gap-0">
        <AdminSidebarNav collapsed={collapsed} />
      </SidebarContent>
    </Sidebar>
  );
}
